import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { cx } from '../../lib/cx';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocBlockPair,
  DocRow,
  DocInlineDate,
  DocInlineAresLookup,
  DocActionButton,
  DocSignatures,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  type DocSignature,
} from '../../components/document';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  type CompanyPickResult,
  type AresPickResult,
} from '../../components/ares';
import { StavbaPickerButton, SaveStavbaButton, type StavbaPickResult } from '../../components/stavba';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import { useSaveDocument } from '../../api/hooks';
import {
  useRizikaDb,
  cinnostiPodleKategorii,
  riskLevel,
  RISK_LEVELS,
  PRAVDEPODOBNOST,
  NASLEDKY,
  type Riziko,
  type RizikaDb,
  type RiskLevelCode,
} from './rizikaData';
import styles from './RizikaPage.module.css';

/**
 * Registr rizik BOZP — „Identifikace a hodnocení rizik při práci".
 *
 * Nahrazuje původní samostatnou aplikaci `frontend/rizika.html` (iframe
 * s vlastním vzhledem a vlastními záložkami). Teď je to stejný „živý dokument"
 * jako ostatní BOZP formuláře: jedna stránka, editovatelná přímo, se
 * stavbou/zhotovitelem z adresáře, uložením do archivu, autosave konceptem,
 * e-mailem a PDF.
 *
 * Obsah dokumentu (v pořadí tisku):
 *   01 Identifikace (zhotovitel + stavba)   02 Zpracování (kdo vyhodnotil / přezkoumal)
 *   03 Rozsah hodnocení — výběr činností   04 Metodika (P, N, R = P × N)
 *   05 Vyhodnocení rizik — tabulka           06 Seznámení — prezenční listina + podpisy
 *
 * Hlavní změna proti původní verzi: rizika se nevybírají po celých kategoriích
 * (882 řádků pro „Povrchové stavby"), ale po konkrétních činnostech
 * (zednické práce, lešení, zemní práce…). Jednotlivé řádky jdou navíc vyřadit.
 */

const DRAFT_KEY = 'rizika';
const PATH = '/app/bozp/rizika';
const MIN_SIGN_ROWS = 10;

interface Firma {
  firma: string;
  ico: string;
  sidlo: string;
}
interface StavbaInfo {
  nazev: string;
  adresa: string;
  zakazka: string;
}
interface Osoba {
  jmeno: string;
  pozice: string;
}
interface UcastnikRow {
  id: string;
  jmeno: string;
  firma: string;
  datum: string;
  podpis: string;
}
/** Vlastní (uživatelem přidaný) řádek v hlavičkových sekcích 01–04. */
interface VlastniRadek {
  id: string;
  k: string;
  v: string;
}
type SekceId = 's01' | 's02' | 's03' | 's04';

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}
function emptyRow(): UcastnikRow {
  return { id: newId('u'), jmeno: '', firma: '', datum: '', podpis: '' };
}
function todayCz(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${d.getFullYear()}`;
}
function deriveInitials(name: string): string {
  const parts = name
    .replace(/\b(s\.?\s*r\.?\s*o\.?|a\.?\s*s\.?|spol\.?)\b/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** Klíč vybrané činnosti — podle názvů (stabilní i při změně pořadí v DB). */
function selKey(kategorie: string, cinnost: string): string {
  return `${kategorie}|${cinnost}`;
}

const LEVEL_CLASS: Record<RiskLevelCode | '', string> = {
  A: styles.lvlA,
  P: styles.lvlP,
  N: styles.lvlN,
  VN: styles.lvlVN,
  '!': styles.lvlEX,
  '': styles.lvlNone,
};

function LevelBadge({ code, title }: { code: RiskLevelCode | ''; title?: string }) {
  return (
    <span className={cx(styles.badge, LEVEL_CLASS[code])} title={title}>
      {code || '–'}
    </span>
  );
}

export function RizikaPage() {
  const { activeStavba } = useActiveStavba();
  const { db, error: dbError } = useRizikaDb();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const toast = useToast();
  const saveDoc = useSaveDocument();
  const [emailOpen, setEmailOpen] = useState(false);

  // === Obsah dokumentu ===
  const [zhotovitel, setZhotovitel] = useState<Firma>({ firma: '', ico: '', sidlo: '' });
  const [stavba, setStavba] = useState<StavbaInfo>({ nazev: '', adresa: '', zakazka: '' });
  const [datum, setDatum] = useState<string>(todayCz());
  const [zpracoval, setZpracoval] = useState<Osoba>({ jmeno: '', pozice: 'OZO v prevenci rizik' });
  const [prezkoumal, setPrezkoumal] = useState<Osoba>({ jmeno: '', pozice: 'vedoucí zaměstnanec pracoviště' });
  /** Vybrané činnosti — klíče `kategorie|činnost`. */
  const [vybrane, setVybrane] = useState<string[]>([]);
  /** Jednotlivě vyřazená rizika (id z DB). */
  const [vyrazena, setVyrazena] = useState<number[]>([]);
  const [ucastnici, setUcastnici] = useState<UcastnikRow[]>([emptyRow()]);
  const [podpisy, setPodpisy] = useState<{ zpracoval: string; prezkoumal: string }>({ zpracoval: '', prezkoumal: '' });
  /** Skryté standardní řádky hlavičky (klíč `s01.firma`…). Ukládá se s dokumentem. */
  const [skryte, setSkryte] = useState<string[]>([]);
  /** Vlastní řádky po sekcích. Ukládá se s dokumentem. */
  const [vlastni, setVlastni] = useState<Record<SekceId, VlastniRadek[]>>({ s01: [], s02: [], s03: [], s04: [] });

  const isHidden = (key: string) => skryte.includes(key);
  const hide = (key: string) => setSkryte((list) => (list.includes(key) ? list : [...list, key]));
  const hiddenCount = (sekce: SekceId) => skryte.filter((k) => k.startsWith(sekce + '.')).length;
  const restoreSection = (sekce: SekceId) => setSkryte((list) => list.filter((k) => !k.startsWith(sekce + '.')));
  const addVlastni = (sekce: SekceId) =>
    setVlastni((v) => ({ ...v, [sekce]: [...v[sekce], { id: newId('r'), k: '', v: '' }] }));
  const updateVlastni = (sekce: SekceId, id: string, patch: Partial<VlastniRadek>) =>
    setVlastni((v) => ({ ...v, [sekce]: v[sekce].map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
  const removeVlastni = (sekce: SekceId, id: string) =>
    setVlastni((v) => ({ ...v, [sekce]: v[sekce].filter((r) => r.id !== id) }));

  // === Jen UI (neukládá se) ===
  const [openKat, setOpenKat] = useState<Record<number, boolean>>({});
  const [pickQuery, setPickQuery] = useState('');
  const [riskQuery, setRiskQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<'' | RiskLevelCode>('');
  const [showExcluded, setShowExcluded] = useState(false);

  const vybraneSet = useMemo(() => new Set(vybrane), [vybrane]);
  const vyrazenaSet = useMemo(() => new Set(vyrazena), [vyrazena]);

  // Aktivní stavba ze sidebaru → předvyplnit, dokud je formulář prázdný.
  useEffect(() => {
    if (!activeStavba) return;
    setStavba((s) =>
      s.nazev
        ? s
        : { nazev: activeStavba.nazev, adresa: activeStavba.adresa ?? '', zakazka: activeStavba.zakazka_cislo ?? '' },
    );
  }, [activeStavba]);

  // === Načtení uloženého dokumentu (?id=) / konceptu ===
  function applySnapshot(d: Record<string, unknown>) {
    if (d.zhotovitel) setZhotovitel((z) => ({ ...z, ...(d.zhotovitel as Partial<Firma>) }));
    if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<StavbaInfo>) }));
    if (typeof d.datum === 'string') setDatum(d.datum);
    if (d.zpracoval) setZpracoval((o) => ({ ...o, ...(d.zpracoval as Partial<Osoba>) }));
    if (d.prezkoumal) setPrezkoumal((o) => ({ ...o, ...(d.prezkoumal as Partial<Osoba>) }));
    if (Array.isArray(d.vybrane)) setVybrane((d.vybrane as unknown[]).filter((x): x is string => typeof x === 'string'));
    if (Array.isArray(d.vyrazena)) setVyrazena((d.vyrazena as unknown[]).filter((x): x is number => typeof x === 'number'));
    if (Array.isArray(d.ucastnici)) {
      const rows = (d.ucastnici as Array<Record<string, unknown>>).map((r) => ({
        id: newId('u'),
        jmeno: String(r.jmeno ?? ''),
        firma: String(r.firma ?? ''),
        datum: String(r.datum ?? ''),
        podpis: String(r.podpis ?? ''),
      }));
      setUcastnici(rows.length ? rows : [emptyRow()]);
    }
    if (d.podpisy) setPodpisy((p) => ({ ...p, ...(d.podpisy as Partial<typeof podpisy>) }));
    if (Array.isArray(d.skryte)) setSkryte((d.skryte as unknown[]).filter((x): x is string => typeof x === 'string'));
    if (d.vlastni && typeof d.vlastni === 'object') {
      const src = d.vlastni as Record<string, unknown>;
      const next: Record<SekceId, VlastniRadek[]> = { s01: [], s02: [], s03: [], s04: [] };
      (Object.keys(next) as SekceId[]).forEach((sekce) => {
        if (Array.isArray(src[sekce])) {
          next[sekce] = (src[sekce] as Array<Record<string, unknown>>).map((r) => ({
            id: newId('r'),
            k: String(r.k ?? ''),
            v: String(r.v ?? ''),
          }));
        }
      });
      setVlastni(next);
    }
  }

  const docData = useMemo(
    () => ({
      zhotovitel,
      stavba,
      datum,
      zpracoval,
      prezkoumal,
      vybrane,
      vyrazena,
      ucastnici: ucastnici.map(({ jmeno, firma, datum: dt, podpis }) => ({ jmeno, firma, datum: dt, podpis })),
      podpisy,
      skryte,
      vlastni: {
        s01: vlastni.s01.map(({ k, v }) => ({ k, v })),
        s02: vlastni.s02.map(({ k, v }) => ({ k, v })),
        s03: vlastni.s03.map(({ k, v }) => ({ k, v })),
        s04: vlastni.s04.map(({ k, v }) => ({ k, v })),
      },
    }),
    [zhotovitel, stavba, datum, zpracoval, prezkoumal, vybrane, vyrazena, ucastnici, podpisy, skryte, vlastni],
  );

  const { editId } = useDocEditor(applySnapshot);

  const draft = useDocDraft<Record<string, unknown>>(DRAFT_KEY, editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docData]);

  // === Odvozená data z DB ===
  const skupiny = useMemo(() => (db ? cinnostiPodleKategorii(db) : []), [db]);

  /** Rizika vybraných činností, seřazená: kategorie → činnost (abecedně) → id. */
  const vybranaRizika = useMemo(() => {
    if (!db) return [] as Riziko[];
    const order = new Map<string, number>();
    let i = 0;
    for (const g of skupiny) for (const c of g.cinnosti) order.set(selKey(g.nazev, db.cinnosti[c.cin]), i++);
    return db.rizika
      .filter((r) => vybraneSet.has(selKey(db.kategorie[r.kat], db.cinnosti[r.cin])))
      .sort((a, b) => {
        const oa = order.get(selKey(db.kategorie[a.kat], db.cinnosti[a.cin])) ?? 0;
        const ob = order.get(selKey(db.kategorie[b.kat], db.cinnosti[b.cin])) ?? 0;
        return oa - ob || a.id - b.id;
      });
  }, [db, skupiny, vybraneSet]);

  const zahrnuta = useMemo(() => vybranaRizika.filter((r) => !vyrazenaSet.has(r.id)), [vybranaRizika, vyrazenaSet]);
  const pocetVyrazenych = vybranaRizika.length - zahrnuta.length;

  const stats = useMemo(() => {
    const c: Record<RiskLevelCode, number> = { A: 0, P: 0, N: 0, VN: 0, '!': 0 };
    for (const r of zahrnuta) {
      const code = riskLevel(r.p, r.n).code;
      if (code) c[code] += 1;
    }
    return c;
  }, [zahrnuta]);

  /** Pořadové číslo v tisku — jen zahrnutá rizika, průběžně. */
  const cislo = useMemo(() => {
    const m = new Map<number, number>();
    zahrnuta.forEach((r, i) => m.set(r.id, i + 1));
    return m;
  }, [zahrnuta]);

  /** Řádky k zobrazení na obrazovce (filtr hledání / úrovně / vyřazených). */
  const zobrazena = useMemo(() => {
    if (!db) return [] as Riziko[];
    const q = riskQuery.trim().toLowerCase();
    return vybranaRizika.filter((r) => {
      if (!showExcluded && vyrazenaSet.has(r.id)) return false;
      if (levelFilter && riskLevel(r.p, r.n).code !== levelFilter) return false;
      if (q) {
        const hay = `${db.zdroje[r.zdroj]} ${r.nebezpeci} ${r.opatreni} ${r.oopp} ${db.cinnosti[r.cin]}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [db, vybranaRizika, riskQuery, levelFilter, showExcluded, vyrazenaSet]);

  // === Akce: výběr činností ===
  function toggleCinnost(key: string) {
    setVybrane((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]));
  }
  function setKategorie(katNazev: string, keys: string[], on: boolean) {
    setVybrane((list) => {
      const rest = list.filter((k) => !k.startsWith(`${katNazev}|`));
      return on ? [...rest, ...keys] : rest;
    });
  }
  function toggleRiziko(id: number) {
    setVyrazena((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  }

  // === Účastníci seznámení ===
  const addRow = () => setUcastnici((list) => [...list, emptyRow()]);
  const updateRow = (id: string, patch: Partial<UcastnikRow>) =>
    setUcastnici((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id: string) => setUcastnici((list) => (list.length > 1 ? list.filter((r) => r.id !== id) : list));

  // === Uložení ===
  const title = `Registr rizik — ${stavba.nazev || activeStavba?.nazev || 'stavba'}`;
  async function handleSave() {
    try {
      await saveDoc.mutateAsync({
        id: editId ?? undefined,
        doc_type: 'rizika',
        title,
        stavba: stavba.nazev || activeStavba?.nazev,
        firma: zhotovitel.firma,
        data_json: { ...docData, pocet_rizik: zahrnuta.length },
      });
      draft.clear();
      toast.success('Registr rizik uložen do archivu.');
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
    }
  }

  const pdfName = `Registr_rizik_${stavba.nazev || 'stavba'}.pdf`;

  const signatureItems: DocSignature[] = [
    {
      label: 'Vyhodnocení provedl',
      name: zpracoval.jmeno,
      role: zpracoval.pozice || '—',
      image: podpisy.zpracoval || undefined,
      onSign: (dataUrl) => setPodpisy((p) => ({ ...p, zpracoval: dataUrl })),
    },
    {
      label: 'Přezkoumal',
      name: prezkoumal.jmeno,
      role: prezkoumal.pozice || '—',
      image: podpisy.prezkoumal || undefined,
      onSign: (dataUrl) => setPodpisy((p) => ({ ...p, prezkoumal: dataUrl })),
    },
  ];

  /** Vlastní řádky sekce: popisek i hodnota editovatelné, ✕ řádek smaže (ne skryje). */
  function renderVlastni(sekce: SekceId) {
    return vlastni[sekce].map((r) => (
      <DocRow
        key={r.id}
        k={
          <input
            type="text"
            className={styles.customKey}
            value={r.k}
            onChange={(e) => updateVlastni(sekce, r.id, { k: e.target.value })}
            placeholder="Popisek"
            aria-label="Popisek vlastního řádku"
          />
        }
        value={r.v}
        onChange={(v) => updateVlastni(sekce, r.id, { v })}
        placeholder="Hodnota"
        onRemove={() => removeVlastni(sekce, r.id)}
        removeTitle="Odebrat řádek"
      />
    ));
  }

  const statusText =
    vybrane.length === 0
      ? 'Bez vybraných činností'
      : `${vybrane.length} ${plural(vybrane.length, 'činnost', 'činnosti', 'činností')} · ${zahrnuta.length} rizik`;

  const screenBlanks = Math.max(0, MIN_SIGN_ROWS - ucastnici.length);

  return (
    <>
      <DocLayout
        breadcrumb={['BOZP', 'Registr rizik']}
        title="Registr rizik BOZP"
        status={vybrane.length > 0 ? 'done' : 'draft'}
        statusText={statusText}
        actions={
          <>
            <NewDocButton draftKey={DRAFT_KEY} path={PATH} />
            <SavedDocsButton docType="rizika" title="Uložené registry rizik" currentId={editId} path={PATH} />
            <Button
              variant="secondary"
              leading="⎙"
              title="PDF na šířku (A4) — tabulka rizik je široká"
              onClick={() => printToPdf(pdfName)}
            >
              PDF
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        {draft.pending && (
          <DraftBanner savedAt={draft.pending.savedAt} onRestore={restoreDraft} onDiscard={draft.clear} />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: deriveInitials(zhotovitel.firma),
              name: zhotovitel.firma || '—',
              address:
                [zhotovitel.sidlo, zhotovitel.ico ? `IČO ${zhotovitel.ico}` : ''].filter(Boolean).join(' · ') || '—',
            }}
            tag="△ Registr rizik"
            title={
              <>
                Identifikace a hodnocení rizik při práci
                <span style={{ display: 'block', fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                  {stavba.nazev ? `pro stavbu / pracoviště ${stavba.nazev}` : 'pro stavbu / pracoviště —'}
                </span>
              </>
            }
            meta="Hodnocení rizik při práci dle směrnice Rady 89/391/EHS a § 102 zákoníku práce"
            footer={{
              left: zhotovitel.firma || '—',
              center: `Registr rizik · ${datum}`,
              right: stavba.zakazka ? `Zakázka: ${stavba.zakazka}` : '',
            }}
          >
            {/* Značka pro printToPdf: tento dokument se tiskne na šířku. */}
            <div data-doc-landscape>
              <DocBlockPair>
                <DocBlock
                  idx="01"
                  title="Zaměstnavatel / zhotovitel"
                  hiddenCount={hiddenCount('s01')}
                  onRestoreHidden={() => restoreSection('s01')}
                  actions={
                    <>
                      <DocActionButton onClick={() => addVlastni('s01')} title="Přidat vlastní řádek">
                        + Řádek
                      </DocActionButton>
                      <CompanyPickerButton
                        preferRole="zhotovitel"
                        title="Načíst zhotovitele z adresáře"
                        onPick={(c: CompanyPickResult) => setZhotovitel({ firma: c.nazev, ico: c.ico, sidlo: c.adresa })}
                      />
                      <SaveCompanyButton
                        title="Uložit zhotovitele do adresáře"
                        build={() => ({
                          role: 'zhotovitel',
                          nazev: zhotovitel.firma,
                          ico: zhotovitel.ico,
                          dic: '',
                          adresa: zhotovitel.sidlo,
                        })}
                      />
                    </>
                  }
                >
                  {!isHidden('s01.firma') && (
                    <DocRow
                      k="Firma"
                      value={zhotovitel.firma}
                      onChange={(v) => setZhotovitel((z) => ({ ...z, firma: v }))}
                      placeholder="Název společnosti"
                      onRemove={() => hide('s01.firma')}
                    />
                  )}
                  {!isHidden('s01.ico') && (
                    <DocRow
                      k="IČO"
                      mono
                      value={zhotovitel.ico}
                      onChange={(v) => setZhotovitel((z) => ({ ...z, ico: v }))}
                      placeholder="12345678"
                      actions={
                        <DocInlineAresLookup
                          ico={zhotovitel.ico}
                          onResult={(a: AresPickResult) =>
                            setZhotovitel((z) => ({ ...z, firma: a.nazev || z.firma, sidlo: a.adresa || z.sidlo, ico: a.ico || z.ico }))
                          }
                        />
                      }
                      onRemove={() => hide('s01.ico')}
                    />
                  )}
                  {!isHidden('s01.sidlo') && (
                    <DocRow
                      k="Sídlo"
                      value={zhotovitel.sidlo}
                      onChange={(v) => setZhotovitel((z) => ({ ...z, sidlo: v }))}
                      placeholder="Ulice, PSČ Město"
                      onRemove={() => hide('s01.sidlo')}
                    />
                  )}
                  {renderVlastni('s01')}
                </DocBlock>

                <DocBlock
                  idx="02"
                  title="Stavba / pracoviště"
                  hiddenCount={hiddenCount('s02')}
                  onRestoreHidden={() => restoreSection('s02')}
                  actions={
                    <>
                      <DocActionButton onClick={() => addVlastni('s02')} title="Přidat vlastní řádek">
                        + Řádek
                      </DocActionButton>
                      <StavbaPickerButton
                        title="Načíst stavbu ze seznamu"
                        onPick={(p: StavbaPickResult) =>
                          setStavba({ nazev: p.nazev, adresa: p.adresa, zakazka: p.zakazka_cislo })
                        }
                      />
                      <SaveStavbaButton
                        title="Uložit stavbu do adresáře staveb"
                        build={() => ({
                          nazev: stavba.nazev.trim(),
                          adresa: stavba.adresa.trim(),
                          zakazka_cislo: stavba.zakazka.trim() || undefined,
                          datum_zahajeni: '',
                        })}
                      />
                    </>
                  }
                >
                  {!isHidden('s02.nazev') && (
                    <DocRow
                      k="Stavba"
                      value={stavba.nazev}
                      onChange={(v) => setStavba((s) => ({ ...s, nazev: v }))}
                      placeholder="Název stavby / pracoviště"
                      onRemove={() => hide('s02.nazev')}
                    />
                  )}
                  {!isHidden('s02.adresa') && (
                    <DocRow
                      k="Adresa"
                      value={stavba.adresa}
                      onChange={(v) => setStavba((s) => ({ ...s, adresa: v }))}
                      placeholder="Adresa staveniště"
                      onRemove={() => hide('s02.adresa')}
                    />
                  )}
                  {!isHidden('s02.zakazka') && (
                    <DocRow
                      k="Zakázka"
                      mono
                      value={stavba.zakazka}
                      onChange={(v) => setStavba((s) => ({ ...s, zakazka: v }))}
                      placeholder="Z-2026/… (volitelné)"
                      onRemove={() => hide('s02.zakazka')}
                    />
                  )}
                  {!isHidden('s02.datum') && (
                    <DocRow
                      k="Datum vyhodnocení"
                      mono
                      value={datum}
                      onChange={setDatum}
                      placeholder="dd. mm. rrrr"
                      actions={<DocInlineDate value={datum} onChange={setDatum} />}
                      onRemove={() => hide('s02.datum')}
                    />
                  )}
                  {renderVlastni('s02')}
                </DocBlock>
              </DocBlockPair>

              <DocBlockPair>
                <DocBlock
                  idx="03"
                  title="Vyhodnocení provedl"
                  hiddenCount={hiddenCount('s03')}
                  onRestoreHidden={() => restoreSection('s03')}
                  actions={
                    <DocActionButton onClick={() => addVlastni('s03')} title="Přidat vlastní řádek">
                      + Řádek
                    </DocActionButton>
                  }
                >
                  {!isHidden('s03.jmeno') && (
                    <DocRow
                      k="Jméno a příjmení"
                      value={zpracoval.jmeno}
                      onChange={(v) => setZpracoval((o) => ({ ...o, jmeno: v }))}
                      placeholder="Jméno zpracovatele"
                      onRemove={() => hide('s03.jmeno')}
                    />
                  )}
                  {!isHidden('s03.pozice') && (
                    <DocRow
                      k="Pracovní pozice"
                      value={zpracoval.pozice}
                      onChange={(v) => setZpracoval((o) => ({ ...o, pozice: v }))}
                      placeholder="OZO v prevenci rizik"
                      onRemove={() => hide('s03.pozice')}
                    />
                  )}
                  {renderVlastni('s03')}
                </DocBlock>
                <DocBlock
                  idx="04"
                  title="Přezkoumal"
                  hiddenCount={hiddenCount('s04')}
                  onRestoreHidden={() => restoreSection('s04')}
                  actions={
                    <DocActionButton onClick={() => addVlastni('s04')} title="Přidat vlastní řádek">
                      + Řádek
                    </DocActionButton>
                  }
                >
                  {!isHidden('s04.jmeno') && (
                    <DocRow
                      k="Jméno a příjmení"
                      value={prezkoumal.jmeno}
                      onChange={(v) => setPrezkoumal((o) => ({ ...o, jmeno: v }))}
                      placeholder="Jméno přezkoumatele"
                      onRemove={() => hide('s04.jmeno')}
                    />
                  )}
                  {!isHidden('s04.pozice') && (
                    <DocRow
                      k="Pracovní pozice"
                      value={prezkoumal.pozice}
                      onChange={(v) => setPrezkoumal((o) => ({ ...o, pozice: v }))}
                      placeholder="vedoucí zaměstnanec pracoviště"
                      onRemove={() => hide('s04.pozice')}
                    />
                  )}
                  {renderVlastni('s04')}
                </DocBlock>
              </DocBlockPair>

              {/* ---------- 05 Rozsah hodnocení ---------- */}
              <DocBlock
                idx="05"
                title="Rozsah hodnocení — činnosti na stavbě"
                actions={
                  vybrane.length > 0 ? (
                    <DocActionButton onClick={() => setVybrane([])} title="Zrušit celý výběr činností">
                      ✕ Zrušit výběr
                    </DocActionButton>
                  ) : undefined
                }
              >
                {dbError && <div className={styles.empty}>Databázi rizik se nepodařilo načíst: {dbError}</div>}
                {!db && !dbError && <div className={styles.empty}>Načítám databázi rizik…</div>}
                {db && (
                  <>
                    <div className={styles.pickScreen}>
                      <div className={styles.pickTools}>
                        <input
                          type="text"
                          className={styles.pickSearch}
                          placeholder="Hledat činnost… (zednické, lešení, výkopy)"
                          value={pickQuery}
                          onChange={(e) => setPickQuery(e.target.value)}
                        />
                        <span className={styles.pickHint}>
                          Zaškrtněte činnosti, které se na stavbě provádějí. Rizika se doplní do tabulky níže.
                        </span>
                      </div>
                      {skupiny.map((g) => {
                        const q = pickQuery.trim().toLowerCase();
                        const visible = q ? g.cinnosti.filter((c) => c.nazev.toLowerCase().includes(q)) : g.cinnosti;
                        if (q && visible.length === 0) return null;
                        const keys = g.cinnosti.map((c) => selKey(g.nazev, c.nazev));
                        const selectedN = keys.filter((k) => vybraneSet.has(k)).length;
                        const open = q ? true : !!openKat[g.kat];
                        return (
                          <div key={g.kat} className={styles.cat}>
                            <div
                              className={styles.catHead}
                              onClick={() => setOpenKat((o) => ({ ...o, [g.kat]: !o[g.kat] }))}
                              role="button"
                              aria-expanded={open}
                            >
                              <span className={styles.catCaret}>{open ? '▼' : '▶'}</span>
                              <span className={styles.catName}>{g.nazev}</span>
                              <span className={cx(styles.catCount, selectedN > 0 && styles.has)}>
                                {selectedN} / {g.cinnosti.length} činností
                              </span>
                              <button
                                type="button"
                                className={styles.catBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setKategorie(g.nazev, keys, selectedN < keys.length);
                                }}
                              >
                                {selectedN < keys.length ? 'Vybrat vše' : 'Zrušit'}
                              </button>
                            </div>
                            {open && (
                              <div className={styles.chips}>
                                {visible.map((c) => {
                                  const key = selKey(g.nazev, c.nazev);
                                  const on = vybraneSet.has(key);
                                  return (
                                    <button
                                      key={key}
                                      type="button"
                                      className={cx(styles.chip, on && styles.on)}
                                      onClick={() => toggleCinnost(key)}
                                      aria-pressed={on}
                                    >
                                      <span className={styles.chipBox} aria-hidden>
                                        {on ? '✓' : ''}
                                      </span>
                                      {c.nazev}
                                      <span className={styles.chipN}>{c.pocet}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Tisková rekapitulace výběru */}
                    <div className={styles.pickPrint}>
                      {vybrane.length === 0 ? (
                        <div className={styles.empty}>Nebyly vybrány žádné činnosti.</div>
                      ) : (
                        skupiny
                          .map((g) => ({
                            g,
                            names: g.cinnosti.filter((c) => vybraneSet.has(selKey(g.nazev, c.nazev))).map((c) => c.nazev),
                          }))
                          .filter((x) => x.names.length > 0)
                          .map(({ g, names }) => (
                            <div key={g.kat} className={styles.pickPrintRow}>
                              <span className={styles.pickPrintKat}>{g.nazev}</span>
                              <span>{names.join(', ')}</span>
                            </div>
                          ))
                      )}
                    </div>
                  </>
                )}
              </DocBlock>

              {/* ---------- 06 Metodika ---------- */}
              <DocBlock idx="06" title="Metodika hodnocení">
                <div className={styles.metodika}>
                  Vyhledávání rizik, zjišťování jejich příčin a zdrojů a opatření k jejich odstranění je provedeno
                  bodovou metodou Výzkumného ústavu bezpečnosti práce — odhadem pravděpodobnosti ohrožení (P) a možných
                  následků (N). Podle tabulky postupuje každý odpovědný pracovník pověřený řízením práce na svěřeném
                  úseku, od vedoucího projektu po předáka, při kontrole pracoviště z hlediska BOZP.
                  <div className={styles.metodikaGrid}>
                    <div>
                      <div className={styles.metodikaH}>Pravděpodobnost ohrožení (P)</div>
                      <table className={styles.metodikaTbl}>
                        <tbody>
                          {PRAVDEPODOBNOST.map((t, i) => (
                            <tr key={t}>
                              <td>{i + 1}</td>
                              <td>{t}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <div className={styles.metodikaH}>Možné následky ohrožení (N)</div>
                      <table className={styles.metodikaTbl}>
                        <tbody>
                          {NASLEDKY.map((t, i) => (
                            <tr key={t}>
                              <td>{i + 1}</td>
                              <td>{t}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className={styles.metodikaFormula}>Míra rizika R = P × N</div>
                  <table className={styles.metodikaTbl}>
                    <tbody>
                      {RISK_LEVELS.map((l) => (
                        <tr key={l.code}>
                          <td>
                            <LevelBadge code={l.code} />
                          </td>
                          <td style={{ width: 60 }}>
                            {l.from}–{l.to}
                          </td>
                          <td style={{ width: 150 }}>{l.label}</td>
                          <td style={{ width: 110 }}>H: {l.h}</td>
                          <td>{l.opatreni}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DocBlock>

              {/* ---------- 07 Vyhodnocení rizik ---------- */}
              <DocBlock
                idx="07"
                title="Vyhodnocení rizik"
                hiddenCount={pocetVyrazenych}
                onRestoreHidden={() => setVyrazena([])}
              >
                <div className={styles.stats}>
                  <span className={styles.statsTotal}>{zahrnuta.length} rizik</span>
                  {RISK_LEVELS.map((l) => (
                    <span key={l.code} className={styles.stat} title={l.label}>
                      <LevelBadge code={l.code} />
                      <span className={styles.statN}>{stats[l.code]}</span>
                      <span className="sd-phone-hide">{l.label.toLowerCase()}</span>
                    </span>
                  ))}
                </div>

                {vybranaRizika.length > 0 && (
                  <div className={cx(styles.filterBar, 'docNoPrint')}>
                    <input
                      type="text"
                      className={styles.pickSearch}
                      placeholder="Hledat v rizicích…"
                      value={riskQuery}
                      onChange={(e) => setRiskQuery(e.target.value)}
                    />
                    <select
                      className={styles.filterSelect}
                      value={levelFilter}
                      onChange={(e) => setLevelFilter(e.target.value as '' | RiskLevelCode)}
                    >
                      <option value="">Všechny úrovně</option>
                      {RISK_LEVELS.map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.code} — {l.label}
                        </option>
                      ))}
                    </select>
                    <label className={styles.filterCheck}>
                      <input type="checkbox" checked={showExcluded} onChange={(e) => setShowExcluded(e.target.checked)} />
                      zobrazit vyřazená ({pocetVyrazenych})
                    </label>
                  </div>
                )}

                {vybranaRizika.length === 0 ? (
                  <div className={styles.empty}>
                    Tabulka je prázdná — vyberte činnosti v sekci 05 „Rozsah hodnocení".
                  </div>
                ) : zobrazena.length === 0 ? (
                  <div className={cx(styles.empty, 'docNoPrint')}>Filtru neodpovídá žádné riziko.</div>
                ) : (
                  <div className={styles.tableScroll}>
                    <RizikaTable
                      db={db!}
                      rows={zobrazena}
                      cislo={cislo}
                      vyrazena={vyrazenaSet}
                      onToggle={toggleRiziko}
                    />
                  </div>
                )}
              </DocBlock>

              {/* ---------- 08 Seznámení ---------- */}
              <DocBlock
                idx="08"
                title="Seznámení s hodnocením rizik"
                actions={
                  <DocActionButton onClick={addRow} title="Přidat účastníka">
                    + Přidat účastníka
                  </DocActionButton>
                }
              >
                <div className={styles.signIntro}>
                  Byl(a) jsem seznámen(a) s hodnocením rizik při práci na stavbě{' '}
                  <strong>{stavba.nazev || '—'}</strong>. Obsahu jsem porozuměl(a), dotazy byly zodpovězeny. Svým
                  podpisem stvrzuji, že s tímto dokumentem prokazatelně seznámím mnou řízené zaměstnance na stavbě.
                </div>
                <table className={styles.signTbl}>
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}>#</th>
                      <th>Jméno a příjmení</th>
                      <th>Firma / pozice</th>
                      <th style={{ width: 110 }}>Datum</th>
                      <th style={{ width: 160 }}>Podpis</th>
                      <th style={{ width: 32 }} className="docNoPrint" />
                    </tr>
                  </thead>
                  <tbody>
                    {ucastnici.map((r, i) => (
                      <tr key={r.id}>
                        <td className={styles.num}>{i + 1}</td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={r.jmeno}
                            onChange={(e) => updateRow(r.id, { jmeno: e.target.value })}
                            placeholder="Jan Novák"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={r.firma}
                            onChange={(e) => updateRow(r.id, { firma: e.target.value })}
                            placeholder="Firma s.r.o. / zedník"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={r.datum}
                            onChange={(e) => updateRow(r.id, { datum: e.target.value })}
                            placeholder="dd. mm. rrrr"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cellInput}
                            value={r.podpis}
                            onChange={(e) => updateRow(r.id, { podpis: e.target.value })}
                            placeholder="(podpis)"
                          />
                        </td>
                        <td className="docNoPrint" style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className={styles.rowRemove}
                            onClick={() => removeRow(r.id)}
                            title="Odebrat účastníka"
                            aria-label="Odebrat účastníka"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                    {Array.from({ length: screenBlanks }).map((_, bi) => (
                      <tr key={`blank-${bi}`} aria-hidden>
                        <td className={styles.num}>{ucastnici.length + bi + 1}</td>
                        <td>
                          <span className={styles.cellBlank} />
                        </td>
                        <td>
                          <span className={styles.cellBlank} />
                        </td>
                        <td>
                          <span className={styles.cellBlank} />
                        </td>
                        <td>
                          <span className={styles.cellBlank} />
                        </td>
                        <td className="docNoPrint" />
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className={styles.signMeta}>
                  Vyhodnocení provedl: {zpracoval.jmeno || '—'}
                  {zpracoval.pozice ? `, ${zpracoval.pozice}` : ''} · Přezkoumal: {prezkoumal.jmeno || '—'}
                  {prezkoumal.pozice ? `, ${prezkoumal.pozice}` : ''} · {datum}
                </div>
              </DocBlock>

              <DocSignatures items={signatureItems} />
            </div>
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="rizika"
        docTitle={title}
        defaultSubject={`${title} (${datum})`}
        meta={`Stavba: ${stavba.nazev || '—'} · ${zahrnuta.length} rizik`}
      />
    </>
  );
}

/** Tabulka rizik seskupená kategorie → činnost. */
function RizikaTable({
  db,
  rows,
  cislo,
  vyrazena,
  onToggle,
}: {
  db: RizikaDb;
  rows: Riziko[];
  cislo: Map<number, number>;
  vyrazena: Set<number>;
  onToggle: (id: number) => void;
}) {
  const body: ReactNode[] = [];
  let lastKat = -1;
  let lastCin = '';
  for (const r of rows) {
    const cinKey = `${r.kat}:${r.cin}`;
    if (r.kat !== lastKat) {
      lastKat = r.kat;
      lastCin = '';
      body.push(
        <tr key={`kat-${r.kat}`} className={styles.katRow}>
          <td colSpan={9}>{db.kategorie[r.kat]}</td>
        </tr>,
      );
    }
    if (cinKey !== lastCin) {
      lastCin = cinKey;
      const n = rows.filter((x) => x.kat === r.kat && x.cin === r.cin && !vyrazena.has(x.id)).length;
      body.push(
        <tr key={`cin-${cinKey}`} className={styles.cinRow}>
          <td colSpan={9}>
            {db.cinnosti[r.cin]}
            <span className={styles.cinRowN}>{n}</span>
          </td>
        </tr>,
      );
    }
    const lvl = riskLevel(r.p, r.n);
    const excluded = vyrazena.has(r.id);
    body.push(
      <tr key={r.id} className={cx(excluded && styles.excluded)}>
        <td className={styles.num}>
          <input
            type="checkbox"
            className={cx(styles.rowChk, 'docNoPrint')}
            checked={!excluded}
            onChange={() => onToggle(r.id)}
            title={excluded ? 'Zařadit zpět do registru' : 'Vyřadit z registru'}
          />
          <span>{excluded ? '–' : cislo.get(r.id)}</span>
        </td>
        <td>{db.zdroje[r.zdroj]}</td>
        <td>{r.nebezpeci}</td>
        <td className={styles.center}>{r.p || ''}</td>
        <td className={styles.center}>{r.n || ''}</td>
        <td className={styles.center}>{lvl.r || ''}</td>
        <td className={styles.center}>
          <LevelBadge code={lvl.code} title={`${lvl.label} · hodnocení ${lvl.h}`} />
        </td>
        <td>{r.opatreni}</td>
        <td>{r.oopp}</td>
      </tr>,
    );
  }
  return (
    <table className={styles.tbl}>
      <colgroup>
        <col className={styles.colNum} />
        <col className={styles.colZdroj} />
        <col className={styles.colNeb} />
        <col className={styles.colPN} />
        <col className={styles.colPN} />
        <col className={styles.colR} />
        <col className={styles.colH} />
        <col />
        <col className={styles.colOopp} />
      </colgroup>
      <thead>
        <tr>
          <th>#</th>
          <th>Zdroj rizika</th>
          <th>Identifikace nebezpečí</th>
          <th title="Pravděpodobnost">P</th>
          <th title="Následky">N</th>
          <th title="Míra rizika R = P × N">R</th>
          <th title="Hodnocení">H</th>
          <th>Bezpečnostní opatření</th>
          <th>Předepsané OOPP</th>
        </tr>
      </thead>
      <tbody>{body}</tbody>
    </table>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n >= 2 && n <= 4) return few;
  return many;
}
